/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React from 'react';
import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import {
    object, shape, bool,
} from 'prop-types';
import { Link } from 'react-router-dom';
import { useUIDSeed } from 'react-uid';
import { compose } from 'recompose';
import { connect, useSelector } from 'react-redux';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import ReactHtmlParser from 'react-html-parser';
import Data from './ViewData';
import ContactUsBannerContainer from '../ContactUsBanner/ContactUsBannerContainer';
import GraphqlSeoHeadTags from '../../GraphqlCommonComponents/GraphqlSeoHeadTags/GraphqlSeoHeadTags';
import { getFeatureFlags } from '../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import triggerPopChatEvent from '../../../MultiBrandChatController/AigoChatWidgetWrapperV2/popOpenChat';
import { getProfileInfo } from '../../../../../state/ducks/App/ducks/Common/Common-Selectors';
import { getOperatingSystem } from '../../../../../state/ducks/App/ducks/Config/Config-Helpers';
import { getCurrentBrandId } from '../../../../pages/Account/utils/featureSelectors';

const styles = (theme) => ({
    container: {
        [theme.breakpoints.down('sm')]: {
            maxWidth: theme.palette.tiers.container.maxWidth.downSmall,
            margin: '0 auto',
        },
        [theme.breakpoints.down('md')]: {
            maxWidth: theme.palette.tiers.container.maxWidth.downMedium,
            margin: '0 auto',
        },
        [theme.breakpoints.up('md')]: {
            maxWidth: 980,
            margin: '0 auto',
        },
        [theme.breakpoints.up('lg')]: {
            maxWidth: 980,
            margin: '0 auto',
        },
    },
    title: {
        textAlign: 'center',
        '& h2': {
            fontSize: 28,
        },
        '& p': {
            fontSize: 15,
            margin: '30px 0',
            padding: '0px 30px',
            textAlign: 'left',
            '& button': {
                cursor: 'pointer',
                padding: 0,
                fontSize: 15,
                background: theme.palette.common.white,
                borderStyle: 'none',
                textDecoration: 'underline',
            },
        },
    },
    orderOptions: {
        margin: '25px auto',
        padding: '0 16px',
        '& p': {
            color: theme.palette.contentColor,
            padding: '0 10px',
            fontSize: 14,
            marginBottom: 0,
        },
        '& a': {
            color: theme.palette.common.black,
            margin: '0 10px',
            padding: '20px 0',
            display: 'flex',
            fontSize: '16px',
            fontWeight: '700',
            borderBottom: `1px solid ${theme.palette.common.black}`,
            letterSpacing: '0.47px',
            textDecoration: 'none',
            justifyContent: 'space-between',
        },
    },
    breadCrumb: {
        marginTop: 10,
        '& a': {
            color: theme.palette.contentColor,
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            fontSize: 15,
            outline: 'none',
        },
    },
    arrowSign: {
        fontSize: 16,
        verticalAlign: 'middle',
    },
    chatLinkBtn: {
        border: 'none',
        background: 'none',
        cursor: 'pointer',
        textDecoration: 'underline',
        fontSize: '15px',
        fontFamily: theme.typography.fontFamily,
        color: theme.palette.text.primary,
    },
});

const CustomerServiceFlowersPage = ({
    classes, page, brand, featureFlags,
}) => {
    const {
        title, description, orderOptions, seo,
    } = Data;
    const seed = useUIDSeed();
    const isChatEnabled = featureFlags['is-chat-icon-displayed'] || featureFlags['is-conv-ui-enabled'];
    const operatingSystem = getOperatingSystem().toLowerCase();
    const currentBrand = useSelector((state) => getCurrentBrandId(state));
    const customerData = useSelector((state) => state?.member);
    const profile = useSelector(getProfileInfo);
    const userData = {
        firstName: profile?.firstName || '',
        lastName: profile?.lastName || '',
        email: profile?.email || '',
    };
    return (
        <>
            <GraphqlSeoHeadTags seoData={seo} />
            <Grid container className={classes.container}>
                <div className={classes.breadCrumb}><Link to="/customer-service"><ArrowBackIosIcon className={classes.arrowSign} />Help Center / Order Issue</Link></div>
                <Grid className={classes.title} item xs={12}>
                    <h2>{title}</h2>
                    {ReactHtmlParser(description)}
                    <p>
                        If you or your recipient are still not satisfied with the quality of the flowers, you can
                        <Link to="/customer-service-reschedule"> request a redelivery </Link>
                        or {isChatEnabled ? <button type="button" className={classes.chatLinkBtn} onClick={() => triggerPopChatEvent(userData, operatingSystem, currentBrand, featureFlags, customerData)}><span>chat with us.</span></button> : 'chat with us'}
                        . We are standing by and ready to help.
                    </p>
                </Grid>
                <Grid item xs={12}>
                    {orderOptions.map((option) => (
                        <div key={seed(option)} className={classes.orderOptions}>
                            <p>{option.title}</p>
                            {option.option.map((data) => <Link key={seed(data)} to={data.link}><div>{data.title}</div> &#x3E;</Link>)}
                        </div>
                    ))}
                </Grid>
            </Grid>
            <ContactUsBannerContainer page={page} brand={brand} />
        </>
    );
};

CustomerServiceFlowersPage.propTypes = {
    classes: object.isRequired,
    brand: object.isRequired,
    page: object.isRequired,
    featureFlags: shape({
        'is-webchat-enabled': bool,
    }).isRequired,
};

const mapStateToProps = (state) => ({
    featureFlags: getFeatureFlags(state),
});

const enhance = compose(
    connect(mapStateToProps),
    withStyles(styles),
);

export default enhance(CustomerServiceFlowersPage);
